import React, { useEffect, useRef, useState } from "react";
import { assets, trackCards } from "../../content/site";

type TrackCardProps = (typeof trackCards)[number] & {
    isFlipped: boolean;
    onClick: () => void;
    onFlipToggle: () => void;
};

export function TrackCard({
    title,
    accent,
    image,
    review,
    isFlipped,
    onClick,
    onFlipToggle,
}: TrackCardProps) {
    const cardRef = useRef<HTMLDivElement | null>(null);
    const [shouldLoadImage, setShouldLoadImage] = useState(false);
    const frontClass =
        accent === "green"
            ? "bg-[linear-gradient(180deg,#96ea28_1.37%,#90e026_50.35%,#7abe21_100%)] text-black"
            : "bg-[linear-gradient(180deg,#ffffff_1.8%,#eeeeee_50.9%,#d8d8d7_100%)] text-black";
    const backClass = accent === "green" ? "bg-white text-black" : "bg-accent text-black";
    const rotationClass = isFlipped ? "[transform:rotateY(180deg)]" : "";

    const isTouchDevice = () =>
        typeof window !== "undefined" &&
        window.matchMedia("(hover: none), (pointer: coarse)").matches;

    const handleCardClick = () => {
        if (isTouchDevice()) {
            onFlipToggle();
            return;
        }

        onClick();
    };

    useEffect(() => {
        const node = cardRef.current;

        if (!node || shouldLoadImage) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShouldLoadImage(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "120px 0px" }
        );

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [shouldLoadImage]);

    return (
        <div
            ref={cardRef}
            className="group h-[504px] text-left [perspective:1200px] max-[639px]:h-[320px]"
            onClick={handleCardClick}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleCardClick();
                }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={isFlipped}
        >
            <span
                className={[
                    "relative block h-full min-h-[504px] [transform-style:preserve-3d] transition-transform duration-700 [transition-timing-function:cubic-bezier(.2,.8,.2,1)] sm:group-hover:[transform:rotateY(180deg)] sm:group-focus-visible:[transform:rotateY(180deg)] max-[639px]:min-h-[320px]",
                    rotationClass,
                ].join(" ")}
            >
                <span
                    className={[
                        "absolute inset-0 flex flex-col overflow-hidden rounded-[40px] border border-muted p-[42px] [backface-visibility:hidden] max-[639px]:rounded-[24px] max-[639px]:p-6",
                        frontClass,
                    ].join(" ")}
                >
                    <span className="max-w-[300px]">
                        <span className="block max-w-[280px] text-[40px] leading-none font-medium tracking-[-0.04em] uppercase max-md:text-[30px] max-[639px]:max-w-[170px] max-[639px]:text-[24px]">
                            {title}
                        </span>
                    </span>
                    <span
                        className="absolute bottom-[28px] left-[28px] inline-flex h-[75px] w-[75px] max-[639px]:bottom-[23px] max-[639px]:left-[23px] max-[639px]:h-[42px] max-[639px]:w-[42px]"
                        aria-hidden="true"
                    >
                        <img src={assets.plusIcon} alt="" />
                    </span>
                    {shouldLoadImage ? (
                        <img
                            className="absolute bottom-0 right-0 max-w-[302px] object-contain max-[639px]:max-w-[208px]"
                            src={image}
                            alt=""
                            decoding="async"
                        />
                    ) : null}
                </span>
                <span
                    className={[
                        "absolute inset-0 flex flex-col rounded-[40px] border border-muted p-[42px] [backface-visibility:hidden] [transform:rotateY(180deg)] max-[639px]:rounded-[24px] max-[639px]:p-6",
                        backClass,
                    ].join(" ")}
                >
                    <span className="max-w-[300px]">
                        <span className="block max-w-[280px] text-[40px] leading-none font-medium tracking-[-0.04em] uppercase max-md:text-[30px] max-[639px]:max-w-[170px] max-[639px]:text-[24px]">
                            {title}
                        </span>
                    </span>
                    <span className="mt-16 max-w-[420px] text-[22px] leading-[1.45] tracking-[-0.02em] max-[639px]:mt-6 max-[639px]:max-w-[240px] max-[639px]:text-[14px] max-[639px]:leading-[1.35]">
                        {review}
                    </span>
                    <button
                        type="button"
                        className="mt-auto inline-flex h-[43px] w-fit items-center justify-center rounded-full border border-current px-4 text-[14px] leading-none font-medium uppercase max-[639px]:text-[12px]"
                        onClick={(event) => {
                            event.stopPropagation();
                            onFlipToggle();
                            onClick();
                        }}
                    >
                        Открыть спикеров
                    </button>
                </span>
            </span>
        </div>
    );
}
